import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { normalizeText } from '../utils/dataUtils';

const API_URL = process.env.REACT_APP_API_BASE_URL || '';

export default function DaycareCheckin() {
    const { user, authorizedFetch } = useAuth();
    const navigate = useNavigate();
    const _d = new Date();
    const today = [_d.getFullYear(), String(_d.getMonth() + 1).padStart(2, '0'), String(_d.getDate()).padStart(2, '0')].join('-');
    const [dogs, setDogs] = useState([]);
    const [dogId, setDogId] = useState('');
    const [dogSearch, setDogSearch] = useState('');
    const [visitDate] = useState(today);
    const [visitType, setVisitType] = useState('regular');
    const [error, setError] = useState('');
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!user) return;
        (async () => {
            try {
                const dogsRes = await authorizedFetch(`${API_URL}/api/dogs?limit=1000`);
                if (dogsRes.ok) {
                    const data = await dogsRes.json();
                    const dogsList = data.data ? data.data : (Array.isArray(data) ? data : []);
                    setDogs(dogsList);
                }
            } catch (err) {
                console.error(err);
                setError(err.message || 'Error al cargar los perros');
            }
        })();
    }, [user, authorizedFetch]);

    const handleSubmit = async e => {
        e.preventDefault();
        if (submitting) return;
        setError('');
        setSubmitting(true);
        try {
            const res = await authorizedFetch(`${API_URL}/api/daycare/visits`, {
                method: 'POST',
                body: JSON.stringify({
                    dog: dogId,
                    date: visitDate,
                    checkInTime: new Date().toISOString(),
                    visitType,
                })
            });
            if (!res.ok) throw new Error(await res.text());
            navigate('/daycare/checkin/logs');
        } catch (err) {
            console.error(err);
            setError(err.message || 'Failed to check in');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="max-w-md mx-auto p-4">
            <div className="bg-white p-6 rounded shadow">
                <h1 className="text-2xl font-semibold mb-4">Daily Check-In</h1>
                <div className="mb-2">Date: {new Date(visitDate).toLocaleDateString()}</div>
                {error && <div className="text-red-600 mb-2">{error}</div>}

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block font-medium mb-1">Perro</label>
                        <input
                            type="text"
                            className="dog-picker-search"
                            placeholder="Buscar perro..."
                            value={dogSearch}
                            onChange={e => setDogSearch(e.target.value)}
                        />
                        <div className="dog-picker-list">
                            {(() => {
                                const q = normalizeText(dogSearch);
                                const filteredDogs = dogs.filter(d => {
                                    if (!q) return true;
                                    const ownerNames = d.owners?.map(o => o.name || '').join(' ') || '';
                                    return normalizeText(`${d.name || ''} ${d.breed || ''} ${ownerNames}`).includes(q);
                                });

                                if (filteredDogs.length === 0) {
                                    return <div className="dog-picker-empty">No se encontraron perros</div>;
                                }

                                return filteredDogs.map(d => {
                                    const isSelected = dogId === d._id;
                                    const ownerText = d.owners?.length ? ` · ${d.owners[0].name}` : '';
                                    return (
                                        <button
                                            type="button"
                                            key={d._id}
                                            className={`dog-picker-card${isSelected ? ' dog-picker-card--selected' : ''}`}
                                            onClick={() => setDogId(d._id)}
                                        >
                                            <div className="dog-picker-avatar">
                                                {d.pictureUrl || d.picture
                                                    ? <img src={d.pictureUrl || d.picture} alt={d.name} />
                                                    : <span>{d.name?.[0]?.toUpperCase()}</span>
                                                }
                                            </div>
                                            <div className="dog-picker-info">
                                                <div className="dog-picker-name">{d.name}</div>
                                                <div className="dog-picker-breed">{d.breed}{ownerText}</div>
                                            </div>
                                            {isSelected && <span className="dog-picker-check">✓</span>}
                                        </button>
                                    );
                                });
                            })()}
                        </div>
                    </div>

                    {dogId && (
                        <div className="checkin-type-group">
                            <button
                                type="button"
                                className={`checkin-type-btn${visitType === 'regular' ? ' checkin-type-btn--active' : ''}`}
                                onClick={() => setVisitType('regular')}
                            >
                                📥 Check In Regular
                            </button>
                            <button
                                type="button"
                                className={`checkin-type-btn checkin-type-btn--trial${visitType === 'trial' ? ' checkin-type-btn--active checkin-type-btn--trial-active' : ''}`}
                                onClick={() => setVisitType('trial')}
                            >
                                🎟️ Día de Prueba
                            </button>
                        </div>
                    )}

                    {visitType === 'trial' && dogId && (
                        <div className="checkin-trial-banner">
                            🎟️ Este check-in es gratuito — no se descontará ningún día del paquete.
                        </div>
                    )}

                    <button
                        type="submit"
                        disabled={!dogId || submitting}
                        className={`checkin-submit-btn${visitType === 'trial' ? ' checkin-submit-btn--trial' : ''}`}
                    >
                        {submitting ? 'Registrando…' : visitType === 'trial' ? '🎟️ Check In · Día de Prueba' : '📥 Check In'}
                    </button>
                </form>
            </div>
        </div>
    );
}
